import { useCallback, useMemo, useState } from 'react';
import { useInitiatives } from '../../../../api/initiative-client/initiative-client.hooks';
import { useRepositoriesMinimal } from '../../../../api/scm-client/scm-client.hooks';
import { useGoalsMinimal, useOutcomesMinimal } from '../../../../api/strategy-client/strategy-client.hooks';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import type { MentionItem, SelectedMention } from './flora-mention.type';

interface BoardItem {
  id: string;
  name: string;
  projectName: string;
  sprintCount: number | null;
}

interface UseMentionReturn {
  isOpen: boolean;
  searchQuery: string;
  allItems: MentionItem[];
  boards: BoardItem[];
  filteredItems: MentionItem[];
  selectedMentions: SelectedMention[];
  mentionStartIndex: number | null;
  handleInputChange: (value: string, cursorPosition: number) => void;
  handleSelectMention: (item: MentionItem) => {
    newValue: string;
    newCursorPosition: number;
  };
  handleRemoveMention: (mentionId: string) => void;
  closeMentionPopover: () => void;
  clearMentions: () => void;
}

/**
 * Custom hook for handling mention input and selection
 * @param {string} currentValue - The current value of the input
 * @returns {UseMentionReturn} - The return value of the hook
 */
export const useMention = (currentValue: string): UseMentionReturn => {
  const organization = useGlobalStore((state) => state.organization);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const portfolios = useGlobalStore((state) => state.portfolios);
  const projects = useGlobalStore((state) => state.teams);

  // Fetch goals, outcomes, initiatives, and repositories
  const { goals } = useGoalsMinimal();
  const { outcomes } = useOutcomesMinimal();
  const { initiatives } = useInitiatives(portfolio?.id);
  const { repositories } = useRepositoriesMinimal(portfolio?.id);

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(null);
  const [selectedMentions, setSelectedMentions] = useState<SelectedMention[]>([]);

  // Build the list of mentionable items:
  // - Portfolios: Only from current organization
  // - Projects & Boards: Only from current portfolio
  // - Goals & Outcomes: From strategy APIs
  const allMentionItems = useMemo((): MentionItem[] => {
    const items: MentionItem[] = [];

    // Add portfolios from current organization only
    if (organization?.id) {
      portfolios
        .filter((p) => p.organization.id === organization.id)
        .forEach((p) => {
          items.push({
            id: p.id,
            name: p.name,
            type: 'portfolio',
          });
        });
    }

    // Add projects and boards from current portfolio only
    if (projects) {
      projects.forEach((project) => {
        items.push({
          id: project.id,
          name: project.name,
          type: 'project',
        });

        // Add boards (subprojects) from this project
        project.subprojects?.forEach((subproject) => {
          items.push({
            id: subproject.id,
            name: subproject.name,
            type: 'board',
            parentName: project.name,
          });
        });
      });
    }

    // Add goals
    goals.forEach((goal) => {
      items.push({
        id: goal.id,
        name: goal.name,
        type: 'goal',
      });
    });

    // Add outcomes
    outcomes.forEach((outcome) => {
      items.push({
        id: outcome.id,
        name: outcome.name,
        type: 'outcome',
      });
    });

    // Add initiatives
    initiatives?.forEach((initiative) => {
      items.push({
        id: initiative.id,
        name: initiative.name,
        type: 'initiative',
      });
    });

    // Add repositories
    repositories.forEach((repository) => {
      items.push({
        id: repository.id,
        name: repository.name,
        type: 'repository',
      });
    });

    return items;
  }, [organization?.id, portfolios, projects, goals, outcomes, initiatives, repositories]);

  // Build list of boards for sprint selection
  const boardItems = useMemo((): BoardItem[] => {
    const boards: BoardItem[] = [];
    if (projects) {
      projects.forEach((project) => {
        project.subprojects?.forEach((subproject) => {
          boards.push({
            id: subproject.id,
            name: subproject.name,
            projectName: project.name,
            sprintCount: subproject.sprint_count ?? null,
          });
        });
      });
    }
    return boards;
  }, [projects]);

  // Filter items based on search query
  const filteredItems = useMemo((): MentionItem[] => {
    if (!searchQuery) {
      return allMentionItems;
    }

    const lowerQuery = searchQuery.toLowerCase();
    return allMentionItems.filter(
      (item) =>
        item.name.toLowerCase().includes(lowerQuery) ||
        item.type.toLowerCase().includes(lowerQuery) ||
        item.parentName?.toLowerCase().includes(lowerQuery),
    );
  }, [allMentionItems, searchQuery]);

  // Handle input changes to detect @ mentions
  const handleInputChange = useCallback((value: string, cursorPosition: number) => {
    // Sync selectedMentions with actual text content
    // Count how many times each placeholder appears in the text
    const mentionPlaceholderRegex = /\[@([^\]]+)\]/g;
    const placeholderCounts = new Map<string, number>();
    let match;
    while ((match = mentionPlaceholderRegex.exec(value)) !== null) {
      const name = match[1];
      placeholderCounts.set(name, (placeholderCounts.get(name) || 0) + 1);
    }

    // Keep only as many mentions as there are placeholders for each name
    setSelectedMentions((prev) => {
      const seenCounts = new Map<string, number>();
      const filtered = prev.filter((mention) => {
        const maxCount = placeholderCounts.get(mention.name) || 0;
        const currentCount = seenCounts.get(mention.name) || 0;
        if (currentCount < maxCount) {
          seenCounts.set(mention.name, currentCount + 1);
          return true;
        }
        return false;
      });
      return filtered.length !== prev.length ? filtered : prev;
    });

    // Find the last @ before cursor position that isn't part of an existing mention
    let lastAtIndex = -1;
    for (let i = cursorPosition - 1; i >= 0; i--) {
      if (value[i] === '@') {
        // Check if this @ is part of an existing mention (inside a pill placeholder)
        const beforeAt = value.substring(0, i);
        const openBrackets = (beforeAt.match(/\[/g) || []).length;
        const closeBrackets = (beforeAt.match(/\]/g) || []).length;

        // If we're not inside brackets, this is a valid @ trigger
        if (openBrackets === closeBrackets) {
          lastAtIndex = i;
          break;
        }
      }
      // Stop searching if we hit a space or newline (mention query shouldn't span multiple words)
      if (value[i] === ' ' || value[i] === '\n') {
        break;
      }
    }

    if (lastAtIndex !== -1) {
      const query = value.substring(lastAtIndex + 1, cursorPosition);
      // Only open popover if query doesn't contain spaces (single word search)
      if (!query.includes(' ')) {
        setIsOpen(true);
        setSearchQuery(query);
        setMentionStartIndex(lastAtIndex);
        return;
      }
    }

    // Close popover if no @ found or query has spaces
    setIsOpen(false);
    setSearchQuery('');
    setMentionStartIndex(null);
  }, []);

  // Handle selecting a mention from the popover
  const handleSelectMention = useCallback(
    (item: MentionItem): { newValue: string; newCursorPosition: number } => {
      if (mentionStartIndex === null) {
        return {
          newValue: currentValue,
          newCursorPosition: currentValue.length,
        };
      }

      // Replace @query with the mention placeholder
      const beforeMention = currentValue.substring(0, mentionStartIndex);
      const afterMention = currentValue.substring(mentionStartIndex + searchQuery.length + 1); // +1 for @

      // Use a placeholder that will be displayed as a pill
      const mentionPlaceholder = `[@${item.name}]`;
      const newValue = beforeMention + mentionPlaceholder + ' ' + afterMention.trimStart();
      const newCursorPosition = beforeMention.length + mentionPlaceholder.length + 1;

      // Track the selected mention
      const newMention: SelectedMention = {
        ...item,
        startIndex: mentionStartIndex,
        endIndex: mentionStartIndex + mentionPlaceholder.length,
      };

      setSelectedMentions((prev) => [...prev, newMention]);
      setIsOpen(false);
      setSearchQuery('');
      setMentionStartIndex(null);

      return { newValue, newCursorPosition };
    },
    [currentValue, mentionStartIndex, searchQuery],
  );

  // Remove a mention
  const handleRemoveMention = useCallback((mentionId: string) => {
    setSelectedMentions((prev) => prev.filter((m) => m.id !== mentionId));
  }, []);

  // Close the popover
  const closeMentionPopover = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setMentionStartIndex(null);
  }, []);

  // Clear all mentions
  const clearMentions = useCallback(() => {
    setSelectedMentions([]);
  }, []);

  return {
    isOpen,
    searchQuery,
    allItems: allMentionItems,
    boards: boardItems,
    filteredItems,
    selectedMentions,
    mentionStartIndex,
    handleInputChange,
    handleSelectMention,
    handleRemoveMention,
    closeMentionPopover,
    clearMentions,
  };
};
